import { ComponentType } from "react";
import { ScrollFadeIn } from "@/components/effects/ScrollFadeIn";
import {
  NeuralNetworkBg,
  CodeEditorBg,
  ShoppingFlowBg,
  CalendarGridBg,
  ConversionFunnelBg,
  InfrastructureNodesBg,
} from "@/components/effects/service-backgrounds";

const services: {
  title: string;
  description: string;
  Background: ComponentType;
  size: "large" | "medium" | "small";
}[] = [
  {
    title: "AI & Agents",
    description: "Custom AI agents, chatbots, and intelligent automation tools built with the latest models.",
    Background: NeuralNetworkBg,
    size: "large",
  },
  {
    title: "Web Applications",
    description: "Full-stack web apps from SaaS platforms to internal tools—responsive and scalable.",
    Background: CodeEditorBg,
    size: "medium",
  },
  {
    title: "E-commerce",
    description: "Online stores with payment processing, inventory management, and beautiful UX.",
    Background: ShoppingFlowBg,
    size: "medium",
  },
  {
    title: "Booking Systems",
    description: "Appointment scheduling, reservation platforms, and calendar integrations.",
    Background: CalendarGridBg,
    size: "small",
  },
  {
    title: "Landing Pages",
    description: "High-converting sales pages and marketing sites that capture attention.",
    Background: ConversionFunnelBg,
    size: "small",
  },
  {
    title: "Technical Architecture",
    description: "Database design, API integrations, and infrastructure that scales to millions.",
    Background: InfrastructureNodesBg,
    size: "small",
  },
];

const Services = () => {
  return (
    <section id="services" className="py-28 px-4 relative">
      {/* Subtle gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-secondary/30 to-transparent" />

      <div className="max-w-6xl mx-auto relative z-10">
        <ScrollFadeIn>
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-5 font-heading">
              We build <span className="text-gradient-indigo">anything</span> you need
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              From idea to production-ready product. We handle the entire technical journey.
            </p>
          </div>
        </ScrollFadeIn>

        {/* Bento Grid Layout */}
        <ScrollFadeIn delay={100}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 auto-rows-[200px]">
            {services.map((service, index) => {
              const isLarge = service.size === "large";
              const isMedium = service.size === "medium";

              return (
                <div
                  key={index}
                  className={`
                    group relative overflow-hidden rounded-3xl
                    transition-all duration-500 hover:shadow-xl hover:scale-[1.02]
                    ${isLarge ? "lg:col-span-3 lg:row-span-2" : ""}
                    ${isMedium ? "lg:col-span-3" : ""}
                    ${!isLarge && !isMedium ? "lg:col-span-2" : ""}
                  `}
                >
                  {/* Animated background */}
                  <service.Background />

                  {/* Dark overlay for text readability */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />

                  {/* Text content */}
                  <div className="relative z-10 h-full flex flex-col justify-end p-6">
                    <h3 className={`font-semibold mb-2 font-heading text-white ${isLarge ? "text-2xl" : "text-lg"}`}>
                      {service.title}
                    </h3>
                    <p className={`text-white/80 leading-relaxed ${isLarge ? "text-base max-w-md" : "text-sm"}`}>
                      {service.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollFadeIn>
      </div>
    </section>
  );
};

export default Services;
